import React, { useEffect, useState, Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@jotrseatrc/shared-components/common/NotificationContext";
import { getFirestore, getDoc, doc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const LoadingSkeleton = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="h-8 w-1/4 bg-slate-200 rounded animate-pulse mb-4"></div>
      <div className="space-y-3">
        <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse"></div>
        <div className="h-4 w-1/2 bg-slate-200 rounded animate-pulse"></div>
      </div>
    </div>
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="h-8 w-1/3 bg-slate-200 rounded animate-pulse mb-4"></div>
      <div className="space-y-3">
        <div className="h-4 w-2/3 bg-slate-200 rounded animate-pulse"></div>
        <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse"></div>
      </div>
    </div>
  </div>
);

// Preload components
const SectionAdminKegiatan = lazy(() => {
  const preloadPromise = import("@jotrseatrc/shared-components").then(
    (module) => ({ default: module.SectionAdminKegiatan })
  );
  preloadPromise.catch(() => {});
  return preloadPromise;
});

const SectionAdminUser = lazy(() => {
  const preloadPromise = import("@jotrseatrc/shared-components").then(
    (module) => ({ default: module.SectionAdminUser })
  );
  preloadPromise.catch(() => {});
  return preloadPromise;
});

export const Xtrifeinnie = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [isAdmin, setIsAdmin] = useState(false);
  const db = getFirestore();
  const navigate = useNavigate();

  // Check admin status
  useEffect(() => {
    if (!user) {
      showNotification("error", "Mohon masuk sebagai admin terlebih dahulu.");
      navigate("/Login");
      return;
    }

    const checkAdmin = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists() && userDoc.data().role === "admin") {
          setIsAdmin(true);
        } else {
          showNotification(
            "error",
            "Akses ditolak: Anda tidak memiliki izin admin."
          );
          navigate("/");
        }
      } catch (err) {
        console.error("Error checking admin:", err);
        showNotification(
          "error",
          "Gagal memeriksa status admin. Silakan coba lagi."
        );
        navigate("/Login");
      }
    };

    checkAdmin();
  }, [user, db, navigate, showNotification]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!isAdmin) return null;

  return (
    <motion.div
      className="min-h-screen bg-slate-100 px-4 sm:px-6 lg:px-8 pt-10 pb-10 z-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-7xl mx-auto pt-4">
        <Suspense fallback={<LoadingSkeleton />}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <SectionAdminUser user={user} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <SectionAdminKegiatan user={user} />
          </motion.div>
        </Suspense>
      </div>
    </motion.div>
  );
};
